import { useNavigate } from 'react-router-dom';
import type { Place } from '@wingman/shared';
import { CATEGORIES } from '@wingman/shared';
import { getOpenStatus } from '../map/MapPopup.js';
import { getClosingSoon } from '../../lib/signals.js';

interface PlaceCardProps {
  place: Place;
  onClick?: (place: Place) => void;
}

const STATUS_LABEL: Record<string, { label: string; color: string }> = {
  open: { label: 'OPEN', color: 'var(--green)' },
  closed: { label: 'CLSD', color: 'var(--red)' },
  unknown: { label: '—', color: 'var(--text-dim)' },
};

export default function PlaceCard({ place, onClick }: PlaceCardProps) {
  const navigate = useNavigate();

  const handleClick = () => {
    if (onClick) onClick(place);
    else navigate(`/place/${place.id}`);
  };

  const cat = CATEGORIES.find((c) => c.id === place.categoryId);
  const dotColor = cat?.color || 'var(--accent)';
  const status = getOpenStatus(place);
  const statusMeta = STATUS_LABEL[status] ?? STATUS_LABEL['unknown'];
  const closingIn = status === 'open' ? getClosingSoon(place) : null;

  return (
    <button
      type="button"
      className="place-card"
      onClick={handleClick}
      aria-label={`View details for ${place.name}`}
    >
      {/* Data-table row: Name | Category | Rating */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 80px 52px',
          alignItems: 'center',
          gap: 8,
          padding: '9px 12px',
        }}
      >
        {/* Name column */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
          <span
            style={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              flexShrink: 0,
              background: dotColor,
            }}
            aria-hidden="true"
          />
          <span
            style={{
              fontSize: 12,
              fontWeight: 500,
              color: 'var(--text-primary)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {place.name}
          </span>
        </div>

        {/* Category column */}
        <span
          style={{
            fontSize: 10,
            fontWeight: 500,
            color: 'var(--text-muted)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {place.categoryName ?? '—'}
        </span>

        {/* Rating + status */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 6 }}>
          {place.rating !== undefined && place.rating > 0 && (
            <span
              className="mono"
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: 'var(--amber)',
              }}
            >
              {place.rating.toFixed(1)}
            </span>
          )}
          <span
            className="mono"
            style={{
              fontSize: 9,
              fontWeight: 700,
              letterSpacing: '0.06em',
              color: statusMeta.color,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 3,
            }}
          >
            {status === 'open' && (
              <span
                aria-hidden="true"
                style={{
                  width: 5,
                  height: 5,
                  borderRadius: '50%',
                  background: 'var(--green)',
                  animation: 'hud-ping 1.6s ease-in-out infinite',
                }}
              />
            )}
            {closingIn !== null ? `${closingIn}m` : statusMeta.label}
          </span>
        </div>
      </div>
    </button>
  );
}
